"use client";

import { useCallback, useMemo, useState } from "react";
import { type Option, PillGroup } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  VITAL_FIELDS as F,
  formatVitalRecord,
  type PageKind,
  type RecordType,
  type VitalKind,
  type VitalLabels,
  vitalIsReady,
} from "@/lib/tool-kit/citation-sources-books/vital-record-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const VITAL_RECORD_CITATION_GENERATOR_STRINGS = {
  formHeading: "Record details",
  resultsHeading: "Your citations",
  emptyPrompt: "Enter the record's details to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  kindLabel: "What are you citing?",
  kindCertificate: "A birth, death or marriage certificate",
  kindCensus: "A census record",
  recordTypeLabel: "Type of certificate",
  typeBirth: "Birth certificate",
  typeDeath: "Death certificate",
  typeMarriage: "Marriage certificate",
  subjectsLabel: "Name(s) on the certificate",
  subjectsPlaceholder: "Mary Ann Sample",
  subjectsHint:
    "One per line. For a marriage, both spouses. Records of living people are private: ask before you cite one.",
  eventDateLabel: "Date of the birth, death or marriage",
  eventDatePlaceholder: "YYYY-MM-DD",
  numberLabel: "Certificate or file number, as printed",
  numberPlaceholder: "file no. 12345",
  officeLabel: "Office that issued it",
  officePlaceholder: "Ohio Department of Health, Bureau of Vital Statistics",
  copyLabel: "The copy you used",
  copyCertified: "Certified copy",
  copyPhotocopy: "Photocopy",
  copyDigital: "Digital image on a website",
  holderLabel: "Where the copy is held (optional)",
  holderPlaceholder: "in the author's possession",
  holderHint: "Leave blank for a copy you hold yourself.",
  databaseLabel: "Website or database",
  databasePlaceholder: "Ancestry.com",
  urlLabel: "URL",
  urlPlaceholder: "https://www.ancestry.com",
  accessedLabel: "Date you viewed it",
  accessedPlaceholder: "YYYY-MM-DD",
  censusYearLabel: "Census year",
  censusYearPlaceholder: "1940",
  stateLabel: "State",
  statePlaceholder: "Illinois",
  countyLabel: "County",
  countyPlaceholder: "Cook County",
  localityLabel: "City, township or ward",
  localityPlaceholder: "Chicago",
  edLabel: "Enumeration district (ED)",
  edPlaceholder: "103-2425",
  pageKindLabel: "Page numbering",
  pageSheet: "Sheet (handwritten)",
  pageStamped: "Page (stamped)",
  pagePenned: "Page (penned)",
  pageLabel: "Sheet or page number",
  pagePlaceholder: "14-B",
  dwellingLabel: "Dwelling number (optional)",
  familyLabel: "Family number (optional)",
  householdLabel: "Household number (1940, optional)",
  householdPlaceholder: "380",
  lineLabel: "Line number (optional)",
  personLabel: "Person you are citing",
  personPlaceholder: "John Q. Sample",
  microfilmLabel: "NARA microfilm publication",
  microfilmPlaceholder: "T627",
  rollLabel: "Roll number",
  rollPlaceholder: "998",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes and bibliography)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  shortNote: "Shortened note",
  bibliography: "Bibliography",
  apaNote:
    "We found no APA 7 model for a certificate or census schedule. This uses APA's pattern for an archival document: the issuing office as author, the year, an italic title with the number in parentheses, a description in square brackets, and the source. Check it with your instructor.",
  mlaNote:
    "We found no MLA 9 model for a certificate or census schedule. This uses MLA's pattern for an unpublished document: the issuing office as author, a plain descriptive title, the date and number, and the form of the copy. Check it with your instructor.",
  chicagoNote:
    "Chicago has no vital-record model of its own. Historians and genealogists follow its note logic as set out in Evidence Explained, whose census model this follows, with dates in Chicago's form. A certificate follows the Chicago-style genealogy footnotes used in university guides. All conventions, not Chicago examples.",
  exampleCertificate: {
    recordType: "death",
    subjects: "Mary Ann Sample",
    eventDate: "1930-08-04",
    number: "file no. 12345",
    office: "Ohio Department of Health, Bureau of Vital Statistics",
    copy: "certified",
  },
  exampleCensus: {
    censusYear: "1940",
    state: "Illinois",
    county: "Cook County",
    locality: "Chicago",
    ed: "103-2425",
    pageKind: "sheet",
    page: "14-B",
    household: "380",
    person: "John Q. Sample",
    microfilm: "T627",
    roll: "998",
    database: "Archives.gov",
    url: "https://1940census.archives.gov",
    accessed: "2024-03-01",
  },
} as const;

type Strings = typeof VITAL_RECORD_CITATION_GENERATOR_STRINGS;

function textField(
  id: string,
  label: string,
  placeholder?: string,
  hint?: string
): SourceField {
  return { id, kind: "text", label, placeholder, hint };
}

function typeOptions(strings: Strings): readonly Option<RecordType>[] {
  return [
    { value: "birth", label: strings.typeBirth },
    { value: "death", label: strings.typeDeath },
    { value: "marriage", label: strings.typeMarriage },
  ];
}

function copyOptions(strings: Strings): readonly Option[] {
  return [
    { value: "certified", label: strings.copyCertified },
    { value: "photocopy", label: strings.copyPhotocopy },
    { value: "digital", label: strings.copyDigital },
  ];
}

function pageOptions(strings: Strings): readonly Option<PageKind>[] {
  return [
    { value: "sheet", label: strings.pageSheet },
    { value: "stamped", label: strings.pageStamped },
    { value: "penned", label: strings.pagePenned },
  ];
}

function webFields(strings: Strings): SourceField[] {
  return [
    textField(F.database, strings.databaseLabel, strings.databasePlaceholder),
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
    },
    {
      id: F.accessed,
      kind: "date",
      label: strings.accessedLabel,
      placeholder: strings.accessedPlaceholder,
    },
  ];
}

function certificateFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.recordType,
      kind: "select",
      label: strings.recordTypeLabel,
      options: typeOptions(strings),
    },
    {
      id: F.subjects,
      kind: "people",
      label: strings.subjectsLabel,
      placeholder: strings.subjectsPlaceholder,
      hint: strings.subjectsHint,
    },
    {
      id: F.eventDate,
      kind: "date",
      label: strings.eventDateLabel,
      placeholder: strings.eventDatePlaceholder,
    },
    textField(F.number, strings.numberLabel, strings.numberPlaceholder),
    {
      id: F.office,
      kind: "text",
      label: strings.officeLabel,
      placeholder: strings.officePlaceholder,
      wide: true,
    },
    {
      id: F.copy,
      kind: "select",
      label: strings.copyLabel,
      options: copyOptions(strings),
    },
    textField(
      F.holder,
      strings.holderLabel,
      strings.holderPlaceholder,
      strings.holderHint
    ),
    ...webFields(strings),
  ];
}

function censusFields(strings: Strings): SourceField[] {
  return [
    textField(
      F.censusYear,
      strings.censusYearLabel,
      strings.censusYearPlaceholder
    ),
    textField(F.state, strings.stateLabel, strings.statePlaceholder),
    textField(F.county, strings.countyLabel, strings.countyPlaceholder),
    textField(F.locality, strings.localityLabel, strings.localityPlaceholder),
    textField(F.ed, strings.edLabel, strings.edPlaceholder),
    {
      id: F.pageKind,
      kind: "select",
      label: strings.pageKindLabel,
      options: pageOptions(strings),
    },
    textField(F.page, strings.pageLabel, strings.pagePlaceholder),
    textField(F.dwelling, strings.dwellingLabel),
    textField(F.family, strings.familyLabel),
    textField(
      F.household,
      strings.householdLabel,
      strings.householdPlaceholder
    ),
    textField(F.line, strings.lineLabel),
    textField(F.person, strings.personLabel, strings.personPlaceholder),
    textField(
      F.microfilm,
      strings.microfilmLabel,
      strings.microfilmPlaceholder
    ),
    textField(F.roll, strings.rollLabel, strings.rollPlaceholder),
    ...webFields(strings),
  ];
}

/** The widget's strings, as the formatter's labels. */
export function vitalLabels(strings: Strings): VitalLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    shortNote: strings.shortNote,
    bibliography: strings.bibliography,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

export default function VitalRecordCitationGeneratorWidget() {
  const strings = useToolUi(VITAL_RECORD_CITATION_GENERATOR_STRINGS);
  const [kind, setKind] = useState<VitalKind>("certificate");
  const fields = useMemo(
    () =>
      kind === "census" ? censusFields(strings) : certificateFields(strings),
    [strings, kind]
  );
  const labels = useMemo(() => vitalLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatVitalRecord(kind, values, labels),
    [kind, labels]
  );
  const isReady = useCallback(
    (values: SourceValues) => vitalIsReady(kind, values),
    [kind]
  );

  return (
    <SourceCitationBuilder
      example={
        kind === "census" ? strings.exampleCensus : strings.exampleCertificate
      }
      fields={fields}
      format={format}
      intro={
        <PillGroup
          label={strings.kindLabel}
          name="vital-kind"
          onChange={setKind}
          options={[
            { value: "certificate", label: strings.kindCertificate },
            { value: "census", label: strings.kindCensus },
          ]}
          value={kind}
        />
      }
      isReady={isReady}
      strings={strings}
    />
  );
}
